import { css } from "@emotion/css";
import { Avatar, Checkbox, Chip, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ProcessTemplateResource, GitRef, ProcessTemplateVersionResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import * as React from "react";
import semver from "semver/preload";
import { getProcessTemplateIconUrl } from "~/areas/blueprints/ProcessTemplatesPage";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
interface PublishTemplateDialogProps {
    processTemplate: ProcessTemplateResource;
    gitRef: GitRef;
    publishedVersions: ProcessTemplateVersionResource[];
    versionToPublish: string;
    setVersionToPublish: (version: string) => void;
    latestNonPreReleaseVersion: ProcessTemplateVersionResource | undefined;
    isPreRelease: boolean;
    setIsPreRelease: (isPreRelease: boolean) => void;
}
export function PublishTemplateDialog({ processTemplate, gitRef, publishedVersions, versionToPublish, setVersionToPublish, latestNonPreReleaseVersion, isPreRelease, setIsPreRelease }: PublishTemplateDialogProps) {
    const [releaseType, setReleaseType] = useState<ReleaseType>("Major");
    const onReleaseTypeChanged = (newReleaseType: ReleaseType) => {
        setReleaseType(newReleaseType);
        updatePublishVersion(newReleaseType, isPreRelease);
    };
    const onIsPreReleaseChanged = (newIsPreRelease: boolean) => {
        setIsPreRelease(newIsPreRelease);
        updatePublishVersion(releaseType, newIsPreRelease);
    };
    const updatePublishVersion = (releaseType: ReleaseType, isPreRelease: boolean) => {
        if (isPreRelease) {
            if (!latestNonPreReleaseVersion) {
                const latestPreReleaseVersion: ProcessTemplateVersionResource | undefined = publishedVersions[0];
                if (!latestPreReleaseVersion) {
                    setVersionToPublish(`${defaultVersion}-prerelease.0`);
                    return;
                }
                setVersionToPublish(semver.inc(latestPreReleaseVersion.Version, "prerelease", "prerelease") ?? defaultVersion);
                return;
            }
            const latestVersion = getLatestPreReleaseForReleaseType(publishedVersions, releaseType, latestNonPreReleaseVersion) ?? latestNonPreReleaseVersion;
            setVersionToPublish(incrementPreReleaseVersionByReleaseType(latestVersion, releaseType) ?? defaultVersion);
        }
        else {
            const latestVersion = publishedVersions.find((v) => !semver.prerelease(v.Version));
            if (!latestVersion) {
                setVersionToPublish(defaultVersion);
                return;
            }
            setVersionToPublish(incrementVersionByReleaseType(latestVersion, releaseType) ?? defaultVersion);
        }
    };
    const latestVersion = isPreRelease ? getLatestPreReleaseForReleaseType(publishedVersions, releaseType, latestNonPreReleaseVersion) ?? latestNonPreReleaseVersion : latestNonPreReleaseVersion;
    return (<div className={styles.dialogContainer}>
            <div className={styles.publishInfoContainer}>
                <Avatar src={getProcessTemplateIconUrl(processTemplate)} shape={"squared"} size={40} alt={"Process template icon"}/>
                <div className={styles.innerContainer}>
                    <div className={styles.versionAndNameContainer}>
                        <div className={styles.name}>{processTemplate.Name}</div>
                        <div>
                            {latestVersion ? (<span className={styles.versionContainer}>
                                    <span>{latestVersion.Version}</span>
                                    <span>→</span>
                                    <Chip backgroundColor={themeTokens.color.badge.background.info} label={versionToPublish} labelColor={themeTokens.color.badge.text.primary} noMargin={true}/>
                                </span>) : (<span className={styles.versionContainer}>{versionToPublish}</span>)}
                        </div>
                    </div>
                    {<GitRefChip vcsRef={{ GitRef: gitRef }} maxGitRefLength={25}/>}
                </div>
            </div>
            <div>
                <h3 className={styles.releaseTypeHeading}>Release type</h3>
                {latestNonPreReleaseVersion ? null : <div className={styles.firstVersionNote}>The first version of a template will always be a major release.</div>}
                <RadioButtonGroup value={releaseType} onChange={onReleaseTypeChanged} accessibleName="Release type" noMargin={true}>
                    <RadioButton value="Major" label="Major changes (breaking)" disabled={!latestNonPreReleaseVersion}/>
                    <RadioButton value="Minor" label="Minor changes (non-breaking)" disabled={!latestNonPreReleaseVersion}/>
                    <RadioButton value="Patch" label="Patch (bug fixes)" disabled={!latestNonPreReleaseVersion}/>
                </RadioButtonGroup>
            </div>
            <div>
                <h4 className={styles.preReleaseHeading}>Flag as pre-release (optional)</h4>
                <Checkbox value={isPreRelease} onChange={onIsPreReleaseChanged} label="Flag as pre-release" noMargin={true} note="Recommended if you are still testing the template. Pre-release templates are flagged in the Process Editor step selector."/>
            </div>
        </div>);
}
type ReleaseType = "Major" | "Minor" | "Patch";
const defaultVersion = "1.0.0";
export function getLatestPreReleaseForReleaseType(publishedVersions: ProcessTemplateVersionResource[], releaseType: ReleaseType, latestNonPreReleaseVersion: ProcessTemplateVersionResource | undefined): ProcessTemplateVersionResource | undefined {
    if (!latestNonPreReleaseVersion) {
        return publishedVersions[0];
    }
    switch (releaseType) {
        case "Major":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "premajor");
        case "Minor":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "preminor");
        case "Patch":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "prepatch");
    }
}
export function incrementPreReleaseVersionByReleaseType(latestVersion: ProcessTemplateVersionResource, releaseType: ReleaseType) {
    if (semver.prerelease(latestVersion.Version)) {
        return semver.inc(latestVersion.Version, "prerelease", "prerelease");
    }
    switch (releaseType) {
        case "Major":
            return semver.inc(latestVersion.Version, "premajor", "prerelease");
        case "Minor":
            return semver.inc(latestVersion.Version, "preminor", "prerelease");
        case "Patch":
            return semver.inc(latestVersion.Version, "prepatch", "prerelease");
    }
}
export function incrementVersionByReleaseType(latestVersion: ProcessTemplateVersionResource, releaseType: ReleaseType) {
    switch (releaseType) {
        case "Major":
            return semver.inc(latestVersion.Version, "major");
        case "Minor":
            return semver.inc(latestVersion.Version, "minor");
        case "Patch":
            return semver.inc(latestVersion.Version, "patch");
    }
}
const styles = {
    dialogContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[32],
    }),
    publishInfoContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[12],
        backgroundColor: themeTokens.color.background.secondary.default,
        borderRadius: borderRadius.small,
        border: `1px solid ${themeTokens.color.border.primary}`,
        padding: space[12],
    }),
    innerContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
    }),
    versionAndNameContainer: css({
        display: "flex",
        flexDirection: "column",
    }),
    name: css({
        font: text.body.bold.medium,
    }),
    versionContainer: css({
        display: "flex",
        gap: space[6],
        alignItems: "center",
        font: text.body.regular.small,
    }),
    releaseTypeHeading: css({
        font: text.heading.small,
        margin: `0 0 ${space[12]} 0`,
    }),
    preReleaseHeading: css({
        font: text.heading.xSmall,
        color: themeTokens.color.text.tertiary,
        textTransform: "uppercase",
        margin: `0 0 ${space[16]} 0`,
    }),
    firstVersionNote: css({
        font: text.body.regular.medium,
        margin: `0 0 ${space[12]} 0`,
    }),
};
